/**
 * JS Style guide.
 * 
 * Stores lots of variables that relate to the site's style guide.
 */

/** Design system ------------------------------------------- */

/**
 * GENERAL TODOS
 * 
 * - Make everything an array of objects.
 *   We could then use getter functions to return things by key or turn them into options.
 * 
 * - Think through whitelisting, blacklisting, and re-mapping labels, etc. for these.
 */

/**
 * The base spacing.
 */
export const baseSpacing = 8

/**
 * Responsive Breakpoints.
 * Assumes mobile-first approach with each being inclusive min-width.
 */
export const breakpoints = {
    default: {
        label     : 'Mobile (Default)',
        threshold : 0
    },
    portraitTablet: {
        label     : 'Tablet (Portrait)',
        threshold : 768
    },
    landscapeTablet: {
        label     : 'Tablet (Landscape)',
        threshold : 1024
    },
    laptop: {
        label     : 'Laptop',
        threshold : 1280,
    },
    desktop: {
        label     : 'Desktop',
        threshold : 1440
    },
    lg: {
        label     : 'Large',
        threshold : 1920
    },
    xl: {
        label     : 'Extra Large',
        threshold : 2560
    },
    longBoi: {
        label     : 'Long Boi',
        threshold : 3440
    }
}

/**
 * The default breakpoint.
 */
export const defaultBreakpoint = 'default'

/**
 * The breakpoint best associated with the editor experience.
 */
export const editorExperienceBreakpoint = 'laptop'

/**
 * Color options.
 * 
 * All brand colors in the theme.
 * 
 * TODO: Make this an array of objects
 */
export const colors = {

    // Primary
    primary100: { hex: '#f5f0ff', label: 'Primary 100' },
    primary700: { hex: '#8000ff', label: 'Primary 700' },
    primary800: { hex: '#6900d1', label: 'Primary 800' },
    primary900: { hex: '#3d009e', label: 'Primary 900' },

    // Secondary
    secondary100: { hex: '#ecf5fd', label: 'Secondary 100' },
    secondary700: { hex: '#0761df', label: 'Secondary 700' },
    secondary800: { hex: '#004ab3', label: 'Secondary 800' },
    secondary900: { hex: '#003785', label: 'Secondary 900' },

    // Neutral
    neutral000: { hex: '#ffffff', label: 'Neutral 000' },
    neutral100: { hex: '#f6f7f9', label: 'Neutral 100' },
    neutral200: { hex: '#e9edf1', label: 'Neutral 200' },
    neutral300: { hex: '#d1d7e1', label: 'Neutral 300' },
    neutral500: { hex: '#96a5bb', label: 'Neutral 500' },
    neutral600: { hex: '#5a6f8b', label: 'Neutral 600' },
    neutral700: { hex: '#3c4a5d', label: 'Neutral 700' },
    neutral800: { hex: '#2c3644', label: 'Neutral 800' },
    neutral900: { hex: '#1c222b', label: 'Neutral 900' },
    neutral1000: { hex: '#040506', label: 'Neutral 1000' },

    // Success
    success100: { hex: '#eafdf7', label: 'Success 100' },
    success700: { hex: '#0a7c52', label: 'Success 700' },
    success800: { hex: '#056743', label: 'Success 800' },
    success900: { hex: '#08553a', label: 'Success 900' },

    // Caution
    caution100: { hex: '#fff7e8', label: 'Caution 100' },
    caution700: { hex: '#a9530b', label: 'Caution 700' },
    caution800: { hex: '#8b4910', label: 'Caution 800' },
    caution900: { hex: '#743e10', label: 'Caution 900' },

    // Danger
    danger100: { hex: '#feeff0', label: 'Danger 100' },
    danger700: { hex: '#dc2835', label: 'Danger 700' },
    danger800: { hex: '#b2212c', label: 'Danger 800' },
    danger900: { hex: '#8d1921', label: 'Danger 900' },
}

/** Shared property options --------------------------------- */

/**
 * The null option.
 * 
 * Normalized property for null/default options.
 */
export const nullOption = { value: '', label: '— Select —' }

/**
 * The custom option.
 * 
 * Normalized property for custom options.
 */
export const customOption = { value: 'custom', label: 'Custom...' }

/**
 * Horizontal alignment options.
 * 
 * Represents possible values for any component that may have a horizontal alignment property.
 */
export const horizontalAlignments = [
    { value: 'left', label: 'Left' },
    { value: 'right', label: 'Right' },
    { value: 'center', label: 'Center' },
    { value: 'between', label: 'Space Between' },
    { value: 'around', label: 'Space Around' }
]

/**
 * Vertical alignment options.
 * 
 * Represents possible values for any component that may have a vertical alignment property.
 */
export const verticalAlignments = [
    { value: 'stretch', label: 'Stretch' },
    { value: 'top', label: 'Top' },
    { value: 'center', label: 'Center' },
    { value: 'bottom', label: 'Bottom' }
]

/**
 * Named spacing options.
 * 
 * Represents all named spacings in the theme.
 */
export const spacings = [
    { value: 'none', label: 'None' },
    { value: 'paragraph', label: 'Paragraph' },
    { value: 'module', label: 'Module' },
    { value: 'block', label: 'Block' },
    { value: 'section', label: 'Section' }
]

/**
 * Named color context options.
 * 
 * Represents all named color contexts in the theme.
 */
export const colorContexts = [
    { value: 'primaryPressed', label: 'Primary Pressed', color: 'primary900' },
    { value: 'neutralPrimaryBgLight', label: 'Neutral Primary Background (Light)', color: 'neutral000' },
    { value: 'neutralBaseBgLight', label: 'Neutral Base Background (Light)', color: 'neutral100' },
    { value: 'neutralPrimaryBgDark', label: 'Neutral Primary Background (Dark)', color: 'neutral900' }
]

/** Getters ------------------------------------------------- */

/**
 * Generalized getter function for shared properties.
 */
function getSharedOptions( options, whitelist = [], includeNull = true, includeCustom = false ) {

    if ( !! whitelist.length  ) {
        options = options.filter( ( option ) => whitelist.indexOf( option.value ) !== -1 )
    }

    if ( includeNull ) {
        options = [ nullOption, ...options ]
    }

    if ( includeCustom ) {
        options = [ ...options, customOption ]
    }

    return options
}

/**
 * Gets horizontal alignments.
 * 
 * @see getSharedOptions()
 */
export function getHorizontalAlignments( whitelist = [], includeNull = true, includeCustom = false ) {
    return getSharedOptions( horizontalAlignments, whitelist, includeNull, includeCustom );
}

/**
 * Gets vertical alignments.
 * 
 * @see getSharedOptions()
 */
export function getVerticalAlignments( whitelist = [], includeNull = true, includeCustom = false ) {
    return getSharedOptions( verticalAlignments, whitelist, includeNull, includeCustom );
}

/**
 * Gets named spacings.
 * 
 * @see getSharedOptions()
 */
export function getSpacings( whitelist = [], includeNull = true, includeCustom = false ) {
    return getSharedOptions( spacings, whitelist, includeNull, includeCustom );
}

/**
 * Gets color contexts.
 * 
 * @see getSharedOptions()
 */
export function getColorContexts( whitelist = [], includeNull = true, includeCustom = false ) {
    return getSharedOptions( colorContexts, whitelist, includeNull, includeCustom )
}
 